import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { templateApi } from "../services/api";
import {
  ActionTemplate,
  ActionTemplateCreate,
  ActionTemplateFilters,
  ActionTemplateUpdate,
} from "../types/template";

export const useTemplates = (filters: ActionTemplateFilters = {}) => {
  return useQuery(
    ["templates", filters],
    () => templateApi.getTemplates(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useTemplate = (id: number) =>
  useQuery(["template", id], () => templateApi.getTemplate(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ActionTemplateCreate) => templateApi.createTemplate(data),
    {
      onSuccess: (created: ActionTemplate) => {
        queryClient.invalidateQueries(["templates"]);
        toast.success(`Template "${created.name}" created`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create template";
        toast.error(message);
      },
    },
  );
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: ActionTemplateUpdate }) =>
      templateApi.updateTemplate(id, data),
    {
      onSuccess: (updated: ActionTemplate) => {
        queryClient.setQueryData(["template", updated.id], updated);
        queryClient.invalidateQueries(["templates"]);
        toast.success(`Template "${updated.name}" updated`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update template";
        toast.error(message);
      },
    },
  );
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => templateApi.deleteTemplate(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["templates"]);
      toast.success("Template deleted");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete template";
      toast.error(message);
    },
  });
};
